/**
 * src/components/landing/LocaleSwitcher.tsx
 * =============================================================================
 * Dropdown discreto para cambiar el idioma de la UI.
 *
 * Lo monta el footer de la LandingPage junto a los enlaces legales. Detección
 * automática de `i18next-browser-languagedetector` sigue siendo el camino
 * por defecto; este selector permite override manual cuando el navegador
 * de un usuario decide mal (ej. portuguesa con el sistema en inglés).
 *
 * Persistencia: `i18n.changeLanguage()` dispara la cache configurada en
 * `i18n.ts` (`detection.caches: ['localStorage']`), por lo que la elección
 * sobrevive a recargas y a cerrar el navegador.
 *
 * Labels en idioma nativo (Deutsch, Português, etc.) — un usuario ruso
 * que cae en la app en español tiene que poder identificar su idioma
 * sin saber español.
 * =============================================================================
 */

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, ChevronUp, Globe } from 'lucide-react'
import { SUPPORTED_LOCALES, type SupportedLocale } from '@/lib/i18n'

/** Nombre nativo de cada idioma, para que el usuario lo reconozca sin
 *  necesidad de hablar el idioma actual de la UI. */
const NATIVE_NAMES: Record<SupportedLocale, string> = {
  es: 'Español',
  en: 'English',
  de: 'Deutsch',
  fr: 'Français',
  it: 'Italiano',
  pt: 'Português',
}

export function LocaleSwitcher() {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // El idioma activo puede ser 'es', 'en-US', 'pt-BR'… reducimos al subtag
  // base que es lo que cargamos como recurso.
  const current = (i18n.resolvedLanguage ?? 'es').split('-')[0] as SupportedLocale

  // Click fuera cierra. No usamos backdrop — la lista es pequeña y un
  // click en cualquier parte de la UI debería cerrarlo.
  useEffect(() => {
    if (!open) return undefined
    function handler(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Escape cierra (mismo patrón que AccountModal).
  useEffect(() => {
    if (!open) return undefined
    function handler(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open])

  function pick(locale: SupportedLocale) {
    void i18n.changeLanguage(locale)
    setOpen(false)
  }

  return (
    <div ref={containerRef} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t('idioma.cambiar')}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="inline-flex items-center gap-1 px-1.5 h-6 rounded text-zinc-500 hover:text-zinc-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400"
      >
        <Globe className="h-3 w-3" strokeWidth={1.8} />
        <span className="text-[11px] uppercase tracking-wide">{current}</span>
        <ChevronUp className="h-3 w-3" strokeWidth={1.8} />
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label={t('idioma.disponibles')}
          className="absolute bottom-full mb-1 right-0 min-w-[140px] rounded-lg border border-zinc-200 bg-white shadow-lg py-1 z-30"
        >
          {SUPPORTED_LOCALES.map((loc) => {
            const isCurrent = loc === current
            return (
              <li key={loc} role="option" aria-selected={isCurrent}>
                <button
                  type="button"
                  onClick={() => pick(loc)}
                  className="w-full flex items-center justify-between gap-2 px-3 h-8 text-xs text-zinc-700 hover:bg-zinc-50"
                >
                  <span>{NATIVE_NAMES[loc]}</span>
                  {isCurrent && (
                    <Check className="h-3.5 w-3.5 text-zinc-900" strokeWidth={2} />
                  )}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
